import React, { useEffect, useState } from "react";
import { patientFileObjectUrl, patientFileRef } from "../api/patientFiles";

/*
 * Object URL for a patient / staff file (api/patientFiles.js): loaded from the v2
 * file route with the staff token, released when the value changes or the
 * component unmounts. A value that is not one of our files (a blob: preview, the
 * legacy chimlw host) is used as it is. `fallback` (an URL) is used when the
 * file can not be loaded, exactly like the old <img onError> fallbacks did.
 * Returns { url, failed }.
 */
export function usePatientFileUrl(stored, folder, fallback) {
  const [state, setState] = useState({ url: null, failed: false });

  useEffect(() => {
    let alive = true;
    let made = null;
    const ref = patientFileRef(stored, folder);
    if (!ref) {
      setState({ url: fallback || null, failed: !fallback });
      return undefined;
    }
    if (ref.kind === "direct") {
      setState({ url: ref.url, failed: false });
      return undefined;
    }
    setState({ url: null, failed: false });
    patientFileObjectUrl(stored, folder)
      .then((url) => {
        if (!alive) {
          URL.revokeObjectURL(url);
          return;
        }
        made = url;
        setState({ url, failed: false });
      })
      .catch(() => {
        if (alive) setState({ url: fallback || null, failed: !fallback });
      });
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [stored, folder, fallback]);

  return state;
}

/*
 * <img> for a stored file value: <PatientFileImg stored={img.front_url || img.fornt_image}
 * folder="images" fallback={legacyUrl} className=... />. Renders nothing until the
 * file is loaded; calls onError once when it can not be loaded (and there is no
 * fallback), so a caller's spinner can stop.
 */
export default function PatientFileImg({ stored, folder, fallback, onError, alt = "", ...imgProps }) {
  const { url, failed } = usePatientFileUrl(stored, folder, fallback);

  useEffect(() => {
    if (failed && typeof onError === "function") onError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failed]);

  if (!url) return null;
  return <img src={url} alt={alt} onError={onError} {...imgProps} />;
}
